/**
 * Just enough Markdown for league documents (the rulebook): headings, ordered
 * and bulleted lists, paragraphs, blockquotes, horizontal rules, and inline
 * bold / italic / code / links.
 *
 * Parsing returns plain data that the renderer turns into React elements, so
 * text authored in the database can never inject markup. Emphasis is not
 * nested: the innermost marker wins and the rest stays literal.
 */

const HEADING = /^(#{1,6})\s+(.*)$/;
const ORDERED_ITEM = /^(\d+)[.)]\s+(.*)$/;
const BULLET_ITEM = /^[-*+]\s+(.*)$/;
const HORIZONTAL_RULE = /^([-*_])\1{2,}$/;
const QUOTE = /^>\s?(.*)$/;

const INLINE = /(\*\*|__)([\s\S]+?)\1|(\*|_)([\s\S]+?)\3|`([^`]+)`|\[([^\]]*)\]\(([^)\s]+)\)/g;

/** Links are limited to schemes a browser can safely follow from user text. */
export const safeHref = (href) => {
  const trimmed = String(href ?? "").trim();
  return /^(https?:\/\/|mailto:|tel:|\/)/i.test(trimmed) ? trimmed : null;
};

export const parseInline = (text) => {
  const source = String(text ?? "");
  const nodes = [];
  let cursor = 0;
  let match;

  INLINE.lastIndex = 0;
  while ((match = INLINE.exec(source)) !== null) {
    if (match.index > cursor) {
      nodes.push({ type: "text", text: source.slice(cursor, match.index) });
    }
    if (match[2] != null) nodes.push({ type: "strong", text: match[2] });
    else if (match[4] != null) nodes.push({ type: "em", text: match[4] });
    else if (match[5] != null) nodes.push({ type: "code", text: match[5] });
    else nodes.push({ type: "link", text: match[6], href: match[7] });
    cursor = match.index + match[0].length;
  }

  if (cursor < source.length) {
    nodes.push({ type: "text", text: source.slice(cursor) });
  }
  return nodes;
};

export const parseMarkdown = (markdown) => {
  const blocks = [];
  let paragraph = null;
  let list = null;

  const flushParagraph = () => {
    if (!paragraph) return;
    blocks.push({ type: paragraph.type, text: paragraph.lines.join(" ") });
    paragraph = null;
  };

  const flushList = () => {
    if (!list) return;
    blocks.push(list);
    list = null;
  };

  const flush = () => {
    flushParagraph();
    flushList();
  };

  for (const rawLine of String(markdown ?? "").split(/\r?\n/)) {
    const line = rawLine.trim();

    if (!line) {
      flush();
      continue;
    }

    if (HORIZONTAL_RULE.test(line)) {
      flush();
      blocks.push({ type: "rule" });
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      flush();
      blocks.push({
        type: "heading",
        level: heading[1].length,
        text: heading[2].trim(),
      });
      continue;
    }

    const ordered = ORDERED_ITEM.exec(line);
    const bullet = ordered ? null : BULLET_ITEM.exec(line);
    if (ordered || bullet) {
      flushParagraph();
      const isOrdered = Boolean(ordered);
      if (!list || list.ordered !== isOrdered) {
        flushList();
        list = {
          type: "list",
          ordered: isOrdered,
          start: isOrdered ? Number(ordered[1]) : 1,
          items: [],
        };
      }
      list.items.push((isOrdered ? ordered[2] : bullet[1]).trim());
      continue;
    }

    const quote = QUOTE.exec(line);
    if (quote) {
      flushList();
      if (paragraph?.type !== "quote") {
        flushParagraph();
        paragraph = { type: "quote", lines: [] };
      }
      paragraph.lines.push(quote[1].trim());
      continue;
    }

    // Plain text continues whatever block is open (lazy continuation).
    if (list) {
      list.items[list.items.length - 1] += ` ${line}`;
      continue;
    }
    if (paragraph) paragraph.lines.push(line);
    else paragraph = { type: "paragraph", lines: [line] };
  }

  flush();
  return blocks;
};
